import { z } from "zod";

/**
 * Townsy Downs, the racing venue protocol (ADR-0016). Races run continuously while anyone is at the track:
 * betting (RACE_PHASE_MS.betting) -> the race (one lap, about 25 s) -> results (RACE_PHASE_MS.results) -> the next race.
 * Eight horses per race with numbers 1-8; win bets only, with play chips (never money, coins or Townsy Credit).
 *
 * The server decides everything: the field, the odds (from hidden ratings), the race (simulated at the tick rate with
 * server randomness) and the payouts. The browser sends intents only: `bet`, `cancel`, `sit`, `stand`.
 */
export const RACE_HORSE_COUNT = 8;
/** Phase lengths (milliseconds). The race itself lasts until every horse has crossed the line (about 25 s). */
export const RACE_PHASE_MS = { betting: 45_000, results: 10_000 } as const;
/** A single bet: 1..50 play chips (and never more than you have). */
export const RACE_MAX_STAKE_PER_BET = 50;
/** All your bets on one race together: at most 100 play chips. */
export const RACE_MAX_STAKE_PER_RACE = 100;
/** At most this many open bets per visitor per race. */
export const RACE_MAX_BETS_PER_RACE = 10;
/** The stake buttons offered by the overlay. */
export const RACE_STAKE_CHOICES = [1, 5, 10, 25] as const;
/** Shared positions are broadcast at this rate while the horses run. */
export const RACE_POSITION_HZ = 10;

export const RacingAction = z.discriminatedUnion("type", [
  /** A win bet on horse `horse` (1-8) for `stake` play chips. */
  z.object({ type: z.literal("bet"), horse: z.number().int().min(1).max(RACE_HORSE_COUNT), stake: z.number().int().min(1).max(RACE_MAX_STAKE_PER_BET) }).strict(),
  /** Take back one of your open bets (betting phase only); the stake comes back. */
  z.object({ type: z.literal("cancel"), betId: z.number().int().min(1) }).strict(),
  /** Sit in the nearest free grandstand seat. */
  z.object({ type: z.literal("sit") }).strict(),
  /** Stand up from your seat. */
  z.object({ type: z.literal("stand") }).strict(),
]);
export type RacingAction = z.infer<typeof RacingAction>;

export type RacePhase = "betting" | "racing" | "results";
export type SilkPattern = "solid" | "hoops" | "halves" | "sash" | "stars" | "quarters";

export interface RaceHorse {
  /** Saddle-cloth number 1-8 (also the post position: lane 1 is the inside rail). */
  n: number;
  name: string;
  /** Jockey's silks: body colour, trim colour (sleeves, cap, pattern) and the pattern. */
  silk: { body: string; trim: string; pattern: SilkPattern };
  /** Coat colour of the horse. */
  coat: string;
  /** Posted win odds `num`-`den` (e.g. [5, 2] is 5-2): a winning bet pays stake x (num/den + 1), rounded down. */
  odds: [number, number];
}

export interface RaceResult {
  raceId: number;
  /** Horse numbers in the order they crossed the finish line. */
  order: number[];
  /** Crossing times, milliseconds after the start, in the same order. */
  times: number[];
  /** The winner's name and odds (for the tote board and notices). */
  winner: { n: number; name: string; odds: [number, number] };
}

/** `venue.state` of the race track (sent to everyone: on every change, and 10 times a second while horses move). */
export interface RacingState {
  raceId: number;
  phase: RacePhase;
  /** Milliseconds left in the betting or results phase when this was sent (0 while racing). */
  endsIn: number;
  /** Race clock: milliseconds since the start (0 while betting). Positions below are at this time. */
  clock: number;
  /** The field for this race (index i is horse number i + 1). */
  horses: RaceHorse[];
  /** Distance run by each horse (decimetres along the course from the start), null while lined up at the start. */
  progress: number[] | null;
  /** Horses that crossed the line so far, in order, with their times (ms after the start). */
  finish: Array<{ n: number; t: number }>;
  /** The settled result of this race (results phase), else null. */
  result: RaceResult | null;
  /** The previous race's result (shown on the tote board while betting). */
  last: RaceResult | null;
  /** Occupied grandstand seats: [seat index, actor id]. */
  seats: Array<[number, string]>;
  /** Bets are accepted (betting phase, and betting is switched on). */
  bettingOpen: boolean;
}

export interface RaceBet {
  id: number;
  horse: number;
  stake: number;
  odds: [number, number];
}

/** `venue.private` state of the race track (your own bets, seat and where you may bet). */
export interface RacingPrivate {
  raceId: number;
  /** Your open bets on the current race. */
  bets: RaceBet[];
  /** Your seat index, or null when standing. */
  seat: number | null;
  /** You stand in the betting hall or sit in the grandstand, so a bet would be taken. */
  canBet: boolean;
  /** You are close enough to the grandstand to sit down. */
  nearStands: boolean;
  /** Your result on the last race you bet on (null until then). */
  last: { raceId: number; staked: number; won: number; winner: number } | null;
}

/** Chips paid for a winning bet: stake x (num/den + 1), integer, rounded down. */
export function racePayout(stake: number, odds: readonly [number, number]): number {
  const [num, den] = odds;
  return Math.floor((stake * (num + den)) / den);
}

/** "5-2" */
export function formatRaceOdds(odds: readonly [number, number]): string {
  return `${odds[0]}-${odds[1]}`;
}
